import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Where a screen may put a control of its own, beside the breadcrumb.
 *
 * <h2>Two things are published, not one</h2>
 *
 * The element to portal into, and a way to say that somebody is using it. The second exists
 * because the bar has a **fallback** to show when no screen has filled the slot — Email
 * Warmup's "All accounts" button — and "is anything in there" cannot be asked of the DOM
 * from React without reading it after every render.
 *
 * <p>It is the design's own mechanism: `AppNav.tsx:1588` gates that button on
 * `headerSlotUsers === 0`, so a screen with its own picker replaces the fallback rather
 * than appearing beside it.
 */
interface HeaderSlotContext {
  target: HTMLElement | null;
  /** Called on mount with +1 and on unmount with -1. Null outside a provider. */
  countUser: ((delta: number) => void) | null;
}

const HeaderSlotTarget = createContext<HeaderSlotContext>({ target: null, countUser: null });

export const HeaderSlotProvider = HeaderSlotTarget.Provider;

/**
 * Puts its children in the application header, for as long as the screen is mounted.
 *
 * <p>Registering is an effect rather than a render-time call: a render may be thrown away,
 * and a count incremented during one that never commits is a count that never comes back
 * down. The cleanup runs on unmount, so navigating away releases the slot.
 */
export function HeaderSlot({ children }: { readonly children: ReactNode }) {
  const { target, countUser } = useContext(HeaderSlotTarget);

  useEffect(() => {
    if (!countUser) return undefined;

    countUser(1);
    return () => {
      countUser(-1);
    };
  }, [countUser]);

  return target ? createPortal(children, target) : null;
}
